import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { AppTokenModel, DynamicObjectInterface } from '../../../sdk/type';
import {
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  ButtonIconPosition,
  ButtonStyles,
  Button,
  ButtonSize,
  DropDown,
  Modal,
  ButtonTypes,
  TextAlign,
  Checkbox,
  InputValidations,
  useBreakpoint,
  FAIcon,
  IconStyle,
  ButtonWidth,
  RadioButton,
  Tooltip,
  TooltipPreference,
} from '../../../ui/components';
import ShareDrawer from '../../../ui/components/DrawerAlt/ShareDrawer';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../hooks';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { isValidEmail } from '../../../ui/utils/common';
import { getTranslatedMessage, LoggerService, PAGES, trackPage, sanitizeOrderNumber } from '../../../ui/utils';
import TeamService from '../../../sdk/services/teamService';
import userService from '../../../sdk/services/userService';
import CampaignService from '../../../sdk/services/campaignService';
import { getContent } from '../../../sdk/services/contentService';
import ParticipantService from '../../../sdk/services/participantService';
import ValidationService from '../../../sdk/services/validationService';
import RegistrationResumeService from '../../../sdk/services/registrationResumeService';
import { REACT_APP_COUNTRY, REACT_APP_AMWAY, REACT_APP_ORDER_HISTORY_URL } from '../../../envVars';
import type { OrderData } from '../join-team/register/[loginId]';
import CreateTeamImage from '../../../assets/image/operationCard/Create_Team.png';
import JoinTeamImage from '../../../assets/image/operationCard/Join_Team.png';
import CheckEligibilityImage from '../../../assets/image/operationCard/Check_Eligibility.png';
import CampaignLogo from '../../../assets/image/Campaign_Logo.png';
import styles from './index.module.scss';

interface CreateTeamData {
  teamId: number | null;
  teamName: string;
  description?: string;
  campaignId: string;
  campaignName: string;
  campaignRecId?: number;
  teamLeader?: string;
  coachId?: string;
  teamStartDate: string;
  teamEndDate: string;
  status: string;
  remark?: string;
  participant?: string[];
  checkbox?: string;
  participationType: string;
  campaignParticipationType?: string;
  pin?: number;
}

const initTeamForm: CreateTeamData = {
  teamId: null,
  teamName: '',
  campaignId: '',
  campaignName: '',
  coachId: '',
  teamLeader: '',
  teamStartDate: '',
  teamEndDate: '',
  status: '',
  remark: '',
  participant: [],
  checkbox: '',
  participationType: '',
};

interface CreateTeamProps {
  decodedData: AppTokenModel | null;
}

export const CreateTeam = (props: CreateTeamProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');

  const [masterList, setMasterList] = useState<{
    coachList: DynamicObjectInterface;
    campaignList: DynamicObjectInterface;
  }>();
  const [teamData, setTeamData] = useState<CreateTeamData>(initTeamForm);
  const [teamSize, setTeamSize] = useState(0);
  const [coachEmail, setCoachEmail] = useState<string>('');
  const [joinTeamLink, setJoinTeamLink] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showNoCampaignPopup, setShowNoCampaignPopup] = useState(false);
  const [showShareDrawer, setShowShareDrawer] = useState(false);
  const [validateUser, setValidateUser] = useState(true);
  const [clickedFields, setClickedFields] = useState({
    campaignId: false,
    teamName: false,
    teamLeader: false,
    participant: [],
  });
  const [emailInvalid, setEmailInvalid] = useState<{
    teamLeader: boolean;
    [key: string]: boolean | undefined;
  }>({ teamLeader: false });
  const [requiredEmailOtp, setRequiredEmailOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  // MY-only: order verification gate (mirrors participant join-team eligibility flow)
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [orderNumberValid, setOrderNumberValid] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [invalidOrderMessage, setInvalidOrderMessage] = useState<string>('');
  const [showEligibilityModal, setShowEligibilityModal] = useState(REACT_APP_COUNTRY === 'MY');
  const [campaignFullData, setCampaignFullData] = useState<DynamicObjectInterface>();
  // Order-first eligibility tiles (MY): one tile per ongoing campaign x sub-challenge.
  const [eligibilityTiles, setEligibilityTiles] = useState<DynamicObjectInterface[]>([]);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [orderAlreadyUsed, setOrderAlreadyUsed] = useState(false);
  // The tile the user picked "Create Team" on — locks campaign + subChallengeId.
  const [selectedTile, setSelectedTile] = useState<DynamicObjectInterface | null>(null);
  const [resumeConsumedSkus, setResumeConsumedSkus] = useState<string[]>([]);
  // ContentStack challenge icons keyed by campaignId, used on the eligibility tiles.
  const [campaignIcons, setCampaignIcons] = useState<Record<string, string>>({});
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  // UUID for the create-team -> registration handoff (carries order + sub-challenge).
  const [resumeId, setResumeId] = useState<string>('');
  // Existing handoff row found for the entered order (resume-by-order): lets the
  // user jump back into registration instead of creating a duplicate team.
  const [existingResume, setExistingResume] = useState<DynamicObjectInterface | null>(null);
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const apiCall = useAPICall();

  const validateInputs = useMemo(() => {
    if (!validateUser) {
      return true;
    }
    const { teamName, participant, participationType } = teamData;
    const isAnyFieldEmpty =
      !teamName ||
      Object.keys(participant ?? {}).length !== (teamSize > 0 ? Number(teamSize - 1) : 0) ||
      Object.values(emailInvalid ?? {}).some(value => value === true);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const participantEmailsValid = Object.values(participant ?? {}).every(email => emailRegex.test(email));
    if ((!isAnyFieldEmpty && participantEmailsValid) || participationType === 'ind') {
      return false;
    }

    return true;
  }, [teamData]);

  const handleInputChange = async (name: string, value: DynamicObjectInterface) => {
    if (name.startsWith('participant_')) {
      setTeamData((prevTeamData: DynamicObjectInterface) => ({
        ...prevTeamData!,
        participant: {
          ...prevTeamData.participant,
          [name]: value,
        },
      }));
      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        participant: {
          ...prevClickedFields.participant,
          [name]: value === '',
        },
      }));
      setEmailInvalid((prevData: DynamicObjectInterface) => ({
        ...prevData!,
        [name]: value !== '' && !isValidEmail(value as string),
      }));
    } else {
      if (name === 'checkbox' && teamData?.checkbox) {
        value = '';
      }

      if (name === 'campaignId') {
        setEmailInvalid({ teamLeader: false });
        setTeamData((prevTeamData: DynamicObjectInterface) => ({
          ...prevTeamData!,
          teamLeader: '',
          participant: {},
        }));

        let campaignRecId = masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === value)?.recId;

        let result = await apiCall(CampaignService.getCampaignDetail, `${campaignRecId}`);
        setTeamSize(result?.data?.data?.activities?.participant?.teamSize);
        setCampaignFullData(result?.data?.data);
        // Reset order verification when switching campaigns
        setOrderNumber('');
        setOrderNumberValid(false);
        setVerificationAttempted(false);
        setInvalidOrderMessage('');
      }

      if (name === 'teamLeader') {
        setEmailInvalid((prevData: DynamicObjectInterface) => ({
          ...prevData!,
          [name]: !isValidEmail(value as string),
        }));
      }

      setTeamData(prevTeamData => ({
        ...prevTeamData!,
        [name]: value,
      }));

      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        [name]: value === '',
      }));
    }
  };

  const cacheKey = 'emailOtp';
  const getEmailOtpSettings = async () => {
    try {
      const data = await getContent('email_otp', cacheKey, false);

      setRequiredEmailOtp(data);
    } catch (error) {
      LoggerService.log('Failed to retrieve content data. Using default content.');
    }
  };

  const handleOrderNumberChange = (value: string) => {
    // Keep only digits and commas (order numbers are digits-only per BE /^\d+$/,
    // multiple orders comma-separated). Strip anything else and collapse repeated
    // commas instead of rejecting the whole keystroke — a hard `return` here leaves
    // the controlled input frozen and looks like typing does nothing.
    const strValue = value
      .replace(/[^0-9,]/g, '')
      .replace(/,{2,}/g, ',');
    const orders = strValue.split(',').map(order => order.trim());
    // Fall back to 3 when maxOrderNumber is unset OR 0 — `?? 3` alone keeps a
    // configured/undefined-loaded 0, which truncates every keystroke to "".
    const maxOrderNumber = campaignFullData?.activities?.order?.maxOrderNumber || 3;
    const limited = orders.length > maxOrderNumber ? orders.slice(0, maxOrderNumber).join(',') : strValue;
    setOrderNumber(limited);
    if (verificationAttempted) setVerificationAttempted(false);
    if (orderNumberValid) setOrderNumberValid(false);
  };

  // Pull challenge icons from ContentStack (keyed by campaign_id) for the tiles.
  const fetchCampaignIcons = async () => {
    try {
      const data = await getContent('challenge_details', 'CreateTeamEligibilityIcons', true);
      if (Array.isArray(data)) {
        const map: Record<string, string> = {};
        data.forEach((item: DynamicObjectInterface) => {
          if (item.campaign_id && item.challenge_icon?.url) {
            map[item.campaign_id] = item.challenge_icon.url;
          }
        });
        setCampaignIcons(map);
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve challenge icons. Using default content.');
    }
  };

  // Order-first eligibility: score the order across every ongoing campaign x
  // sub-challenge and render one tile per candidate (eligible / not eligible).
  const handleCheckEligibility = async () => {
    setVerificationAttempted(true);
    if (!orderNumber) {
      setEligibilityChecked(false);
      return;
    }
    setCheckingEligibility(true);
    // sanitizeOrderNumber strips separators/blanks, so an input like "," collapses to
    // "" — which the BE rejects with a 400 (missing salesOrderNumber) before it can
    // return the "order not found" tiles. Fall back to the raw (trimmed) input so the
    // BE receives it, flags it as invalid format, and still returns the challenge tiles.
    const sanitizedOrder = sanitizeOrderNumber(orderNumber) || orderNumber?.trim();
    const payload = {
      aboId: props.decodedData?.loginId,
      salesOrderNumber: sanitizedOrder,
    };
    try {
      // Resume-by-order: if this abo already started registration with this order,
      // surface a resume action instead of letting them create a duplicate team.
      const existing = await apiCall(RegistrationResumeService.find, {
        aboId: props.decodedData?.loginId,
        orderNumber: sanitizedOrder,
      });
      setExistingResume(existing?.data?.success ? existing.data.data : null);

      const response = await apiCall(ValidationService.checkOrderEligibility, payload);
      const result = response.data;
      const tiles: DynamicObjectInterface[] = result?.data?.tiles ?? [];
      // errorCode 102 on every tile means the order was already consumed globally.
      const allUsed = tiles.length > 0 && tiles.every(tile => tile.errorCode === 102);
      setEligibilityTiles(tiles);
      setEligibleCount(result?.eligibleCount ?? tiles.filter(tile => tile.eligible).length);
      setOrderAlreadyUsed(allUsed || result?.errorCode === 102);
      setEligibilityChecked(true);
      // Fetch challenge avatars for every tile (eligible or not).
      if (tiles.length > 0) await fetchCampaignIcons();
    } catch (error) {
      LoggerService.log('Error checking order eligibility:', error);
      setEligibilityTiles([]);
      setEligibleCount(0);
      setOrderAlreadyUsed(false);
      setExistingResume(null);
      setEligibilityChecked(true);
    } finally {
      setCheckingEligibility(false);
    }
  };

  // User picked "Create Team" on an eligible tile — lock that campaign +
  // sub-challenge into the form and proceed to the team-details step.
  const handleSelectTile = async (tile: DynamicObjectInterface) => {
    const campaign = masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === tile.campaignId);
    if (!campaign) {
      adminToast({ message: t('dashboard.create-team.message.unexpectedError'), theme: Theme.ERROR });
      return;
    }
    const participationType =
      typeof campaign.participationType === 'string' && JSON.parse(campaign.participationType)?.includes('team')
        ? 'team'
        : 'ind';

    const detail = await apiCall(CampaignService.getCampaignDetail, `${campaign.recId}`);
    setCampaignFullData(detail?.data?.data);
    setTeamSize(detail?.data?.data?.activities?.participant?.teamSize ?? campaign.teamSize);
    setTeamData(prevTeamData => ({
      ...prevTeamData,
      campaignId: campaign.value,
      campaignName: campaign.campaignName,
      campaignRecId: campaign.recId,
      campaignParticipationType: campaign.participationType,
      participationType,
    }));
    await validUserJoinTeam(campaign.recId, participationType);

    setSelectedTile(tile);
    setResumeConsumedSkus(Array.isArray(tile.consumedSkus) ? tile.consumedSkus : []);
    setOrderNumberValid(true);
    setShowEligibilityModal(false);
  };

  // Resume-by-order: jump straight back into the registration the user already
  // started with this order (team already exists, or solo `ind` flow).
  const handleResumeRegistration = () => {
    if (!existingResume) return;
    const { resumeId: existingId, teamId: resumeTeamId, campaignRecId: resumeCampaignRecId } = existingResume;
    const campaign = masterList?.campaignList?.find(
      (c: DynamicObjectInterface) => c.value === existingResume.campaignId,
    );
    const cmI = resumeCampaignRecId ?? campaign?.recId ?? teamData?.campaignRecId;
    // No teamId on the row => solo (`ind`) registration; otherwise team-leader.
    const tmI = resumeTeamId ?? 'ind';
    const tmL = resumeTeamId ? 1 : 0;
    navigate(`/join-team/register?cmI=${cmI}&tmI=${tmI}&tmL=${tmL}&rsm=${existingId}`);
  };

  // Stash the chosen order + sub-challenge so team-leader registration can be
  // resumed (even after a browser close) with both already locked in. Returns
  // the resumeId to carry in the registration link, or '' if not applicable.
  const createResumeRow = async (teamIdValue: number | null): Promise<string> => {
    if (REACT_APP_COUNTRY !== 'MY' || !selectedTile) return '';
    if (resumeId) return resumeId;
    try {
      const response = await apiCall(RegistrationResumeService.create, {
        teamId: teamIdValue,
        campaignId: selectedTile.campaignId,
        campaignRecId: selectedTile.campaignRecId ?? teamData?.campaignRecId,
        orderNumber: sanitizeOrderNumber(orderNumber),
        subChallengeId: selectedTile.subChallengeId ?? null,
        consumedSkus: resumeConsumedSkus,
      });
      const newId = response?.data?.data?.resumeId ?? '';
      setResumeId(newId);
      return newId;
    } catch (error) {
      LoggerService.log('Error creating registration resume:', error);
      return '';
    }
  };

  const onSave = async () => {
    const payload = {
      campaignId: teamData?.campaignId,
      campaignName: teamData?.campaignName,
      teamName: teamData?.teamName,
      coachId: props.decodedData?.userId,
      memberEmails: [
        ...Object.values(teamData?.participant ?? {}).map(email => ({ email, isTeamLead: false })),
        { email: coachEmail, isTeamLead: true },
      ],
      loginId: props.decodedData?.loginId,
      description: teamData?.remark,
      startDate: new Date().toISOString().slice(0, -1),
      pin: teamData?.pin,
    };

    let result = await apiCall(TeamService.createTeam, payload);
    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: getTranslatedMessage(t, result?.data?.message),
          theme: Theme.ERROR,
        });
      }
    } else {
      const link = result?.data?.link;
      setJoinTeamLink(link);
      // Persist the resume row as soon as the team is created (was previously
      // deferred to the "Join Team" click in handleJoinTeam). This locks the
      // order number the moment a team exists, so it can't be reused to create
      // a duplicate team even if the leader never clicks "Join Team".
      // createResumeRow is idempotent (guards on state resumeId), so the
      // subsequent join-now flow reuses this same resumeId — nothing breaks.
      try {
        const encodedValue = link ? new URLSearchParams(link.split('?')[1]).get('t') : null;
        if (encodedValue) {
          const values = atob(encodedValue).split(',').map(Number);
          await createResumeRow(values[1]);
        }
      } catch (error) {
        LoggerService.log('Error creating registration resume on team create:', error);
      }
      setShowConfirmation(false);
      setShowNotification(true);
    }
  };

  const teamDetailsBtnConfig = [
    {
      title: t('dashboard.create-team.button.continue'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: validateInputs,
      onClick: async () => {
        if (teamData.participationType === 'ind') {
          const rsm = await createResumeRow(null);
          navigate(
            `/join-team/register?tmI=ind&cmI=${teamData?.campaignRecId}${rsm ? `&rsm=${rsm}` : ''}`,
          );
        } else {
          setShowConfirmation(true);
        }
      },
    },
  ];

  const handleJoinTeam = async () => {
    const encodedValue = new URLSearchParams(joinTeamLink.split('?')[1]).get('t');

    if (encodedValue) {
      const decoded = atob(encodedValue);
      const values = decoded.split(',').map(Number);
      const rsm = await createResumeRow(values[1]);
      window.location.href = ` join-team/register?cmI=${values[0]}&tmI=${values[1]}&tmL=${values[2]}${
        rsm ? `&rsm=${rsm}` : ''
      }`;
    } else {
      window.location.href = joinTeamLink;
    }

    setShowNotification(false);
  };

  const confirmationPrompt = (
    <>
      {showConfirmation && (
        <Modal
          primaryButtonLabel={''}
          headline={t('dashboard.create-team.modal.confirmation.teamConfirmation')}
          children={
            <>
              <div
                style={{
                  fontSize: '16px',
                  fontFamily: 'GT-Walsheim-Pro-Bold, sans-serif',
                  lineHeight: '24px',
                  margin: '10px 0',
                }}
              >
                {t('dashboard.create-team.modal.confirmation.confirmation')}
              </div>
              <div style={{ display: 'flex', margin: '32px 0' }}>
                <Checkbox
                  key={'checkbox'}
                  className={styles['checkbox']}
                  checked={teamData.checkbox === 'on'}
                  theme={Theme.BLACK}
                  text={
                    <p
                      id="agree-checkbox"
                      dangerouslySetInnerHTML={{
                        __html: t('dashboard.create-team.modal.confirmation.termsLine1').replace(
                          '{terms-conditions}',
                          `<a href="/terms-and-conditions?cid=${teamData?.campaignId}" target="_blank">${t(
                            'securityPrivacy.terms-condition',
                          )}</a>`,
                        ),
                      }}
                    />
                  }
                  onChange={e => handleInputChange('checkbox', e.target.value)}
                />
                <>
                  {/* {t('admin.teamCreate.termsLine1')} */}
                  <br />
                  {/* {t('admin.teamCreate.termsLine2')} */}
                </>
              </div>
            </>
          }
          closeIcon={false}
          containerWidth={'max-content'}
          textAlign={TextAlign.LEFT}
          primaryButton={
            <Button
              title={t('dashboard.create-team.modal.confirmation.button.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onSave();
              }}
              size={ButtonSize.SMALL}
              theme={Theme.GREEN}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
              disabled={!teamData?.checkbox}
            />
          }
          secondaryButton={
            <Button
              title={t('dashboard.create-team.modal.confirmation.button.back')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowConfirmation(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.GREEN}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  const noCampaignPrompt = (
    <>
      {showNoCampaignPopup && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          containerWidth={isDesktopView ? '60%' : '80%'}
          primaryButton={
            <Button
              title={t('dashboard.create-team.modal.noCampaign.button.backToHomepage')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                navigate(`/`);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={undefined}
        >
          <div>
            <div>
              <img src={JoinTeamImage} alt={`JoinTeamImage`} height={isDesktopView ? '400px' : '200px'} />
            </div>
            <div className={styles['sub-header']}>{t('dashboard.create-team.modal.noCampaign.description')}</div>
          </div>
        </Modal>
      )}
    </>
  );

  const notificationPrompt = (
    <>
      {showNotification && (
        <Modal
          primaryButtonLabel={''}
          containerWidth={'max-content'}
          headline={''}
          closeIcon={true}
          onClose={() => {
            navigate(`/`);
          }}
          textAlign={TextAlign.CENTER}
          primaryButton={
            <Button
              title={t('dashboard.create-team.modal.notification.button.joinTeam')}
              type={ButtonTypes.BUTTON}
              theme={Theme.GREEN}
              onClick={() => {
                handleJoinTeam();
                // setShowNotification(false);
                // window.location.href = joinTeamLink;
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('dashboard.create-team.modal.notification.button.share')}
              type={ButtonTypes.BUTTON}
              theme={Theme.GREEN}
              onClick={() => {
                setShowShareDrawer(true);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              icon={{ iconName: 'share-alt', iconStyle: IconStyle.SOLID }}
              iconPosition={ButtonIconPosition.LEFT}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <div className={styles['modal-container']}>
            <div className={styles['modal-img']}>
              <img src={CreateTeamImage} alt={`createTeamImage`} width="295px" />
            </div>
            <div className={styles['modal-headline']}>{t('dashboard.create-team.modal.notification.teamSetup')}</div>
            <div className={styles['modal-desc']}>{t('dashboard.create-team.modal.notification.sentEmail')}</div>
          </div>
        </Modal>
      )}
    </>
  );

  const participantInput = Array.from({ length: teamSize - 1 }, (_, index) => {
    const plusOne = index;
    return (
      <div className={styles['participant-email-container']}>
        <p className={styles['input-label']}>
          {t('dashboard.create-team.input.participant')} {plusOne + 1}
          <span className={styles['label-red']}>*</span>
        </p>
        <Input
          placeholder={t('dashboard.create-team.input.placeholder.email')}
          key={`${teamData?.campaignId}_participant_${index}`}
          type={InputTypes.TEXT}
          name={`participant_${index + 1}`}
          mlpVersion={MLPVersion.ONE}
          onChange={e => {
            handleInputChange(`participant_${index}`, e.target.value);
          }}
          status={emailInvalid?.[`participant_${index}`] ? InputValidations.INVALID : InputValidations.DEFAULT}
          message={
            emailInvalid?.[`participant_${index}`] ? `${t('dashboard.create-team.message.invalidEmailFormat')}` : ''
          }
        />
      </div>
    );
  });

  const sharePrompt = (
    <>
      {showShareDrawer && (
        <ShareDrawer
          open={!!joinTeamLink}
          onClose={() => setShowShareDrawer(false)}
          title={t('dashboard.create-team.button.shareVia')}
          programMessage={t('dashboard.create-team.message.invitedToJoinCampaign')
            .replace('{campaignName}', teamData?.campaignName)
            .replace('{join-team-link}', joinTeamLink)}
          programUrl={joinTeamLink}
          channels={process.env.REACT_APP_COUNTRY === 'PH' ? ['FaWhatsapp', 'FaViber','FaLink'] : ['FaWhatsapp', 'FaLink']}
          programTitle=""
        />
      )}
    </>
  );

  const renderEligibilityTile = (tile: DynamicObjectInterface, index: number) => {
    // Challenge avatar from ContentStack, falling back to the default campaign logo
    // (same fallback used on the My Progress page).
    const iconUrl = campaignIcons[tile.campaignId] || CampaignLogo;
    const title = tile.subChallengeName || tile.campaignName;
    return (
      <div
        key={`${tile.campaignId}_${tile.subChallengeId ?? 'base'}_${index}`}
        className={`${styles['eligibility-tile']} ${
          tile.eligible ? styles['eligibility-tile--eligible'] : styles['eligibility-tile--ineligible']
        }`}
      >
        <div className={styles['eligibility-tile__header']}>
          <img src={iconUrl} alt={title} className={styles['eligibility-tile__image']} />
          <span
            className={`${styles['eligibility-tile__badge']} ${
              tile.eligible
                ? styles['eligibility-tile__badge--eligible']
                : styles['eligibility-tile__badge--ineligible']
            }`}
          >
            {tile.eligible
              ? t('dashboard.create-team.label.eligible')
              : t('dashboard.create-team.label.notEligible')}
          </span>
        </div>

        {tile.subChallengeName && (
          <div className={styles['eligibility-tile__campaign']}>{tile.campaignName}</div>
        )}
        <div className={styles['eligibility-tile__title']}>{title}</div>
        {tile.subChallengeDescription && (
          <div className={styles['eligibility-tile__description']}>{tile.subChallengeDescription}</div>
        )}

        <div className={styles['eligibility-tile__action']}>
          {tile.eligible ? (
            <span
              className={styles['eligibility-tile__link']}
              role="button"
              tabIndex={0}
              onClick={() => handleSelectTile(tile)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectTile(tile);
                }
              }}
            >
              {t('dashboard.create-team.label.createTeam')}
            </span>
          ) : (
            <span
              className={styles['eligibility-tile__link']}
              role="button"
              tabIndex={0}
              onClick={() =>
                window.open(tile.buyBundleLink || REACT_APP_AMWAY, '_blank', 'noopener,noreferrer')
              }
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  window.open(tile.buyBundleLink || REACT_APP_AMWAY, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              {t('dashboard.create-team.label.buyBundles')}
            </span>
          )}
        </div>
      </div>
    );
  };

  const eligibilityModal = showEligibilityModal ? (
    <Modal
      headline={t('dashboard.create-team.label.checkEligibility')}
      primaryButtonLabel={''}
      closeIcon={true}
      closeOnBackdropClick={false}
      onClose={() => {
        navigate('/');
      }}
      containerWidth={'640px'}
      primaryButton={<></>}
    >
      <div>
        <div className={styles['eligibility-modal__row']}>
          <div className={styles['eligibility-modal__input']}>
            {(() => {
              // After a check, show a solid tick (eligible) or solid cross (not eligible)
              // on the left of the input. "Eligible" = at least one joinable challenge and
              // the order isn't already used; otherwise it's not eligible.
              const isEligible = eligibilityChecked && !orderAlreadyUsed && eligibleCount > 0;
              const isNotEligible = eligibilityChecked && (orderAlreadyUsed || eligibleCount === 0);
              const eligibilityIconProps = isEligible
                ? {
                    status: InputValidations.VALID,
                    inputIcon: { iconName: 'circle-check' as IconName, iconStyle: IconStyle.SOLID },
                  }
                : isNotEligible
                  ? {
                      status: InputValidations.INVALID,
                      inputIcon: { iconName: 'circle-xmark' as IconName, iconStyle: IconStyle.SOLID },
                    }
                  : {};
              return (
                <Input
                  name="eligibilityOrderNumber"
                  label={t('dashboard.join-team.register.input.orderNumber')}
                  value={orderNumber}
                  mlpVersion={MLPVersion.ONE}
                  {...eligibilityIconProps}
                  onChange={e => {
                    handleOrderNumberChange(e.target.value.toString());
                    if (eligibilityChecked) setEligibilityChecked(false);
                    if (existingResume) setExistingResume(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleCheckEligibility();
                  }}
                />
              );
            })()}
          </div>
          <div className={styles['eligibility-modal__verify-link']}>
            <Button
              title={t('dashboard.create-team.label.verify')}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.PURPLE}
              size={ButtonSize.SMALL}
              disabled={!orderNumber || checkingEligibility}
              onClick={handleCheckEligibility}
            />
          </div>
        </div>

        {/* Empty state before a successful check: prompt + illustration + order-history link. */}
        {!eligibilityChecked && (
          <div className={styles['eligibility-empty']}>
            <img
              src={CheckEligibilityImage}
              alt={t('dashboard.create-team.label.enterOrderToStart')}
              className={styles['eligibility-empty__image']}
            />
            <div className={styles['eligibility-empty__title']}>
              {t('dashboard.create-team.label.enterOrderToStart')}
            </div>
            <div className={styles['eligibility-empty__subtitle']}>
              {t('dashboard.create-team.label.needOrderNumber')}{' '}
              <span
                className={styles['eligibility-tile__link']}
                role="button"
                tabIndex={0}
                onClick={() => window.open(REACT_APP_ORDER_HISTORY_URL, '_blank', 'noopener,noreferrer')}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.open(REACT_APP_ORDER_HISTORY_URL, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                {t('dashboard.create-team.label.orderHistory')}
              </span>
            </div>
          </div>
        )}

        {eligibilityChecked && existingResume && (
          <div className={styles['eligibility-modal__resume']}>
            <div className={styles['eligibility-modal__resume-text']}>
              <FAIcon
                iconStyle={IconStyle.SOLID}
                iconName="clock-rotate-left"
                theme={Theme.PURPLE}
                className={styles['eligibility-modal__banner-icon']}
              />
              <span>{t('dashboard.create-team.label.resumeRegistrationPrompt')}</span>
            </div>
            <Button
              title={t('dashboard.create-team.label.resumeRegistration')}
              buttonStyle={ButtonStyles.SOLID}
              theme={Theme.GREEN}
              size={ButtonSize.SMALL}
              onClick={handleResumeRegistration}
              className={styles['eligibility-modal__resume-button']}
            />
          </div>
        )}

        {eligibilityChecked && !existingResume && orderAlreadyUsed && (
          <>
            <div className={styles['eligibility-modal__banner-error']}>
              <FAIcon
                iconStyle={IconStyle.SOLID}
                iconName="circle-exclamation"
                theme={Theme.ERROR}
                className={styles['eligibility-modal__banner-icon']}
              />
              <span>{t('dashboard.create-team.label.orderAlreadyUsed')}</span>
            </div>
            {/* Show the bundle tiles so the user can still buy bundles, like the
                order-not-found / not-eligible case. */}
            {eligibilityTiles.length > 0 && (
              <div className={styles['eligibility-tiles']}>
                {eligibilityTiles.map((tile, index) => renderEligibilityTile(tile, index))}
              </div>
            )}
          </>
        )}

        {eligibilityChecked && !existingResume && !orderAlreadyUsed && eligibilityTiles.length > 0 && (
          <>
            <div
              className={
                eligibleCount > 0
                  ? styles['eligibility-modal__banner']
                  : styles['eligibility-modal__banner-error']
              }
            >
              <FAIcon
                iconStyle={IconStyle.SOLID}
                iconName={eligibleCount > 0 ? 'circle-check' : 'circle-exclamation'}
                theme={eligibleCount > 0 ? Theme.PURPLE : Theme.ERROR}
                className={styles['eligibility-modal__banner-icon']}
              />
              <span>
                {eligibleCount > 0
                  ? (eligibleCount === 1
                      ? t('dashboard.create-team.label.joinChallengesNow')
                      : t('dashboard.create-team.label.joinChallengesNowPlural')
                    ).replace('{count}', String(eligibleCount))
                  : // Order-level failure (invalid format / not found / wrong ABO /
                    // cancelled) => "order not found"; a valid order that simply doesn't
                    // qualify => "does not qualify".
                    eligibilityTiles.every(tile => [101, 103, 105, 107].includes(tile.errorCode))
                    ? t('dashboard.create-team.label.orderNotFound')
                    : t('dashboard.create-team.label.noEligibleChallenges')}
              </span>
            </div>
            <div className={styles['eligibility-tiles']}>
              {eligibilityTiles.map((tile, index) => renderEligibilityTile(tile, index))}
            </div>
          </>
        )}
      </div>
    </Modal>
  ) : null;

  useEffect(() => {
    (async () => {
      let apiList = [
        apiCall(userService.getUserDetails, `${props.decodedData?.userId}`),
        apiCall(CampaignService.getCampaigns),
      ];
      let res = await Promise.all(apiList);

      let masterList = {
        coachList: [
          // ...(res?.[0]?.data?.data || []).map((d: DynamicObjectInterface) => ({
          //   ...d,
          //   id: d.coachID,
          //   title: `${d.coachID} - ${d.name}`,
          // })),
        ],
        campaignList: (
          res?.[1]?.data?.data.filter(
            (i: DynamicObjectInterface) =>
              new Date(i.registerEnd).getTime() > new Date().getTime() &&
              i.status !== 'DELETED' &&
              i.status !== 'CLOSED',
          ) || []
        ).map((d: DynamicObjectInterface) => ({
          ...d,
          value: d.campaignId,
          title: `${d.campaignId} - ${d.campaignName}`,
          campaignName: d.campaignName,
        })),
      };

      setCoachEmail(res?.[0]?.data?.data?.email ? res?.[0]?.data?.data?.email : '');
      setEmailVerified(res?.[0]?.data?.data?.emailVerified === 1 ? true : false);
      setMasterList(masterList);
      // After getting the master list, use the campaignRecId to validate if the user can join the team
      if (masterList?.campaignList?.length <= 0) {
        setShowNoCampaignPopup(true);
      }
    })();

    getEmailOtpSettings();

    const timeout = setTimeout(() => {
      trackPage(PAGES.createTeam);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const initializeTeamData = async () => {
      if (masterList?.campaignList?.length > 0) {
        const firstCampaign = masterList?.campaignList[0];
        const participationType = JSON.parse(firstCampaign.participationType)?.includes('team') ? 'team' : 'ind';

        setTeamData(prevTeamData => ({
          ...prevTeamData,
          campaignId: firstCampaign.value,
          campaignName: firstCampaign.campaignName,
          campaignRecId: firstCampaign.recId,
          campaignParticipationType: firstCampaign.participationType,
          participationType,
        }));
        setTeamSize(firstCampaign?.teamSize);
        const detail = await apiCall(CampaignService.getCampaignDetail, `${firstCampaign.recId}`);
        setCampaignFullData(detail?.data?.data);
        await validUserJoinTeam(firstCampaign.recId, participationType);
      }
    };

    initializeTeamData();
  }, [masterList]);

  const validUserJoinTeam = async (teamRecId: number, participationType: string) => {
    const payload = {
      campaignRecId: teamRecId,
      loginId: props.decodedData?.loginId,
      partyId: props.decodedData?.partyId,
    };
    let validToCreate = await apiCall(ParticipantService.validateJoinTeam, payload);

    setValidateUser(
      participationType === 'ind'
        ? validToCreate?.data?.data?.canEnrollParticipant
        : validToCreate?.data?.data?.canCreateTeam,
    );
    setTeamData(prevTeamData => ({
      ...prevTeamData,
      pin: validToCreate?.data?.data?.pin,
    }));
  };

  if (REACT_APP_COUNTRY === 'MY' && (!orderNumberValid || showEligibilityModal)) {
    return <div className={styles['edit-team']}>{eligibilityModal}</div>;
  }

  return (
    <div className={styles['edit-team']}>
      <div className={styles['card']}>
        <div className={styles['card-header']}>
          <h2>{t('dashboard.create-team.label.title')}</h2>
          {isDesktopView && (
            <>
              <div className={styles['actions-container']}>
                {teamDetailsBtnConfig.map(btn => (
                  <Button key={btn.title} theme={Theme.GREEN} size={ButtonSize.SMALL} {...btn} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className={styles['create-team-form-container']}>
          <div className={styles['dropdown-container']}>
            {/* <div className={styles['dropdown-label']}>
              {t('admin.teams.campaign')} <span className={styles['label-red']}>*</span>
            </div> */}
            <div className={styles['dropdown-test']}>
              <DropDown
                name="campaignId"
                disabledFilter={false}
                // MY only: campaign is already locked in by the eligibility tile the user
                // picked (based on their order), so it's read-only. ID/PH have no
                // eligibility flow and still choose the campaign here.
                disabled={REACT_APP_COUNTRY === 'MY'}
                mlpVersion={MLPVersion.ONE}
                value={
                  masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === teamData?.campaignId)?.title
                }
                dropdownLabel={t('dashboard.create-team.input.campaign')}
                data={masterList?.campaignList}
                configData={{
                  label: 'title',
                  onSelect: async (selectedOption: DropDownData) => {
                    handleInputChange('campaignId', selectedOption.value);
                    handleInputChange('campaignRecId', selectedOption.recId);
                    await validUserJoinTeam(Number(selectedOption.recId), teamData.participationType);
                    handleInputChange('campaignName', selectedOption.campaignName);
                    handleInputChange('campaignParticipationType', selectedOption.participationType);
                    handleInputChange(
                      'participationType',
                      typeof selectedOption.participationType === 'string' &&
                        JSON.parse(selectedOption.participationType)?.includes('team')
                        ? 'team'
                        : 'ind',
                    );
                  },
                }}
                readOnly={false}
              />
              {teamData?.pin === undefined ? (
                <div className={styles['validate-user-container']}>
                  <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                  <div className={styles['error-text']}>{t('dashboard.create-team.message.unexpectedError')}</div>
                </div>
              ) : (
                !validateUser && (
                  <div className={styles['validate-user-container']}>
                    <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                    <div className={styles['error-text']}>{t('dashboard.create-team.message.notFulfilCondition')}</div>
                  </div>
                )
              )}
            </div>
          </div>

          {teamData?.campaignParticipationType &&
            teamData?.campaignParticipationType.includes('team') &&
            teamData?.campaignParticipationType.includes('ind') && (
              <div className={styles['input-container']}>
                <div className={styles['label-text']}>
                  <div>
                    {t('dashboard.create-team.input.joinAs')}
                    <span className={styles['label-red']}> *</span>
                  </div>
                  <div>
                    <Tooltip
                      message={t('dashboard.create-team.input.tooltip.joinAs')}
                      preference={TooltipPreference.BOTTOM_CENTER}
                      className={styles['input-tool-tip']}
                      iconName="circle-exclamation"
                    />
                  </div>
                </div>
                <div className={styles['radio-button-container']}>
                  <RadioButton
                    label={t('dashboard.create-team.input.team')}
                    checked={teamData?.participationType === 'team'}
                    onChange={async () => {
                      teamData.campaignRecId && (await validUserJoinTeam(teamData.campaignRecId, 'team'));
                      handleInputChange('participationType', 'team');
                    }}
                  />

                  <RadioButton
                    label={t('dashboard.create-team.input.individual')}
                    checked={teamData?.participationType === 'ind'}
                    onChange={async () => {
                      teamData.campaignRecId && (await validUserJoinTeam(teamData.campaignRecId, 'ind'));
                      handleInputChange('participationType', 'ind');
                    }}
                  />
                </div>
              </div>
            )}
        </div>

        {teamData?.participationType === 'team' && (
          <div className={styles['create-team-form-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'teamName'}
              labelClassName={styles['team-name-label']}
              label={t('dashboard.create-team.input.teamName')}
              value={teamData?.teamName}
              required
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChange('teamName', e.target.value);
              }}
              placeholder={t('dashboard.create-team.input.placeholder.teamName')}
              status={clickedFields.teamName ? InputValidations.INVALID : InputValidations.DEFAULT}
              message={clickedFields.teamName ? `${t('admin.teamCreate.teamNameRequired')}` : ''}
            />
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'teamLeader'}
              label={t('dashboard.create-team.input.teamLeader')}
              labelClassName={styles['team-leader-label']}
              value={`${props.decodedData?.loginId} ${props.decodedData?.name}`}
              mlpVersion={MLPVersion.ONE}
              disabled={true}
            />
          </div>
        )}
        {/* <div className={styles['create-team-form-container']}>
          <Input
            labelClassName={styles['remarks-test']}
            key={id}
            type={InputTypes.TEXT}
            name={'remark'}
            label={`${t('dashboard.create-team.input.remarks')}`}
            value={teamData?.remark}
            placeholder="Remarks"
            mlpVersion={MLPVersion.ONE}
            // labelPosition={LabelPosition.LEFT}
            onChange={e => {
              handleInputChange('remark', e.target.value);
            }}
          />
        </div> */}
        {teamData?.participationType === 'team' && (
          <div className={styles['card-send-email-invitation']}>
            <div
              className={styles['card-send-email-invitation-title']}
            >{`${t('dashboard.create-team.label.sendEmailInvitation')}`}</div>
            <div className={styles['card-send-email-invitation-desc']}>
              {`${t('dashboard.create-team.label.emailInvitationDescription')?.replace('{teamSize}', (teamSize - 1 || '').toString())}`}
            </div>
            <div className={styles['card-send-email-invitation-input-container']}>
              <div className={styles['participant-email-container']}>
                <p className={styles['input-label']}>
                  {t('dashboard.create-team.input.teamLeader')} <span style={{ color: 'red' }}> *</span> &nbsp;
                  {requiredEmailOtp && (
                    <Tooltip
                      message={t('dashboard.create-team.input.tooltip.email')}
                      className={styles['input-tool-tip']}
                    />
                  )}
                </p>

                <Input
                  key={id}
                  type={InputTypes.TEXT}
                  name={'teamLeader'}
                  value={`${coachEmail} `}
                  placeholder={t('dashboard.create-team.input.placeholder.teamLeader')}
                  mlpVersion={MLPVersion.ONE}
                  disabled={true}
                  onChange={e => {
                    handleInputChange('teamLeader', e.target.value);
                  }}
                  status={!coachEmail ? InputValidations.WARNING : InputValidations.DEFAULT}
                  message={
                    requiredEmailOtp && emailVerified ? (
                      <div className={styles['participant-section-input-message']}>
                        <FAIcon
                          iconName={'circle-check'}
                          theme={Theme.SUCCESS}
                          iconStyle={IconStyle.SOLID}
                          className={styles['modal-reward-delete']}
                        />
                        <div>{t('dashboard.create-team.message.emailVerified')}</div>
                      </div>
                    ) : !coachEmail ? (
                      t('dashboard.create-team.message.teamLeaderEmailMissing')
                    ) : (
                      ''
                    )
                  }
                />
              </div>
              {/* {!coachEmail && (
                <div className={styles['validate-user-container']}>
                  <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                  <div className={styles['error-text']}>
                    {'Please fill up your email address at Profile page before proceeding to create team!'}
                  </div>
                </div>
              )} */}
              {participantInput}
            </div>
          </div>
        )}
        <p
          className={styles['input-message-footnote']}
          dangerouslySetInnerHTML={{
            __html: t('dashboard.create-team.message.joinTeamFootnote').replace(
              '{one-month}',
              `<b>${t('dashboard.create-team.message.oneMonth')}</b>`,
            ),
          }}
        />
        {/* <div className={styles['input-message']}>{`*${t('admin.dashboard.joinTeamFootnote')}`}</div> */}
        {!isDesktopView && (
          <>
            <div className={styles['actions-container-mobile']}>
              {teamDetailsBtnConfig.map(btn => (
                <Button
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  theme={Theme.GREEN}
                  key={btn.title}
                  size={ButtonSize.SMALL}
                  {...btn}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {confirmationPrompt}
      {notificationPrompt}
      {noCampaignPrompt}
      {sharePrompt}
    </div>
  );
};
